import { BOOL4_TYPES, DB_FIELDS_VALID_VALUES } from '~/components/SystemConfiguration/FnolConfiguration/constants';
import copies from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/copies.json';

export interface FieldTypeOption {
  id: string;
  desc: string;
}

/**
 * The field types a global field may declare: every type the FNOL Configuration screen offers,
 * minus the slot-backed ones.
 *
 * Both halves are derived, not listed. The types come from the same `copies.json` the per-
 * configuration screen renders, so a type added there is offered here too; the exclusions come
 * from `DB_FIELDS_VALID_VALUES`, the declaration that makes a type slot-backed in the first
 * place. A hand-written pair of exclusions would be a second place to remember, and the backend
 * refuses these types from its own copy of the same derivation — so a drift would surface as a
 * save error rather than a missing option.
 *
 * Why they are excluded: contact and location occupy one of a fixed set of database columns per
 * entity, and which slots are free differs per configuration. A global field would need the same
 * slot free on every form in its context, which nothing can guarantee at save time.
 */
export const SLOT_BACKED_TYPES: string[] = Object.keys(DB_FIELDS_VALID_VALUES);

export const GLOBAL_FIELD_TYPE_OPTIONS: FieldTypeOption[] = (copies.field_types as FieldTypeOption[]).filter(
  (option) => !SLOT_BACKED_TYPES.includes(option.id)
);

/** Types whose definition carries an `options` list. */
export const TYPES_WITH_OPTIONS = ['select', 'multiselect'];

/**
 * The type list the Type select offers, under the rules the per-configuration
 * `ConfigurationFieldType` applies: `boolean` is not offered for a new field, and a saved field's
 * type is fixed except within the yes/no family, because stored claim values are typed by it.
 */
export const getTypeSelectOptions = (isEdit: boolean, savedType: string): FieldTypeOption[] => {
  if (!isEdit) {
    return GLOBAL_FIELD_TYPE_OPTIONS.filter((option) => option.id !== 'boolean');
  }
  return BOOL4_TYPES.includes(savedType)
    ? GLOBAL_FIELD_TYPE_OPTIONS.filter((option) => BOOL4_TYPES.includes(option.id))
    : GLOBAL_FIELD_TYPE_OPTIONS;
};

export const isTypeLocked = (isEdit: boolean, savedType: string): boolean => isEdit && !BOOL4_TYPES.includes(savedType);

type HiddenAnswers = { disable_na: boolean; disable_unknown: boolean };

/** The narrowed yes/no types are stored as `yes_no_na_unknown` plus the answers they hide, which
 *  is the one yes/no type the backend accepts; the same mapping `ConfigurationFieldType` writes. */
const NARROWED_YES_NO_TYPES: Record<string, HiddenAnswers> = {
  yes_no: { disable_na: true, disable_unknown: true },
  yes_no_na: { disable_na: false, disable_unknown: true },
  yes_no_unknown: { disable_na: true, disable_unknown: false },
};

/** The stored `type` and hidden-answer flags for the type picked in the select. */
export const toStoredType = (type: string): { type: string } & Partial<HiddenAnswers> =>
  type in NARROWED_YES_NO_TYPES ? { type: 'yes_no_na_unknown', ...NARROWED_YES_NO_TYPES[type] } : { type };

/** The select's type for a stored definition: the inverse of `toStoredType`. */
export const toDisplayedType = (definition: Record<string, unknown>): string => {
  const type = String(definition.type ?? '');
  if (type !== 'yes_no_na_unknown') {
    return type;
  }
  const narrowed = Object.entries(NARROWED_YES_NO_TYPES).find(
    ([, flags]) =>
      flags.disable_na === !!definition.disable_na && flags.disable_unknown === !!definition.disable_unknown
  );
  return narrowed ? narrowed[0] : type;
};
